import { BaseRepository } from '~/passporter-services/baseRepository'
import { WallPinEntityMapper } from './entityMapper'
import { ApiWallPin } from './apiTypes'
import { WallPin } from '~/models/WallPin'
import { WallPinApiMapper } from './apiMapper'
import { WallPinCache } from './cache'
import { Page } from '~/models/PaginatedItems'
import { ApiPage, toPage } from '~/passporter-services/shared/pagination'

export class WallPinRepository extends BaseRepository {
	static resource = 'v1/itineraries'
	private static cache = new WallPinCache()

	static async getWallPins(
		tripId: string,
		page: number = 0,
		pageSize: number = 10
	): Promise<Page<WallPin>> {
		const cached = this.cache.getTripPage(tripId, page, pageSize)
		if (cached) return cached
		const response: ApiPage<ApiWallPin> | undefined = await this.get(
			`${this.resource}/${tripId}/wall_pins/`,
			{ page, page_size: pageSize }
		)
		const result = toPage(response!, WallPinEntityMapper.toWallPins(response!.results))
		this.cache.setTripPage(tripId, result, page, pageSize)
		return result
	}

	static async createWallPin(tripId: string, wallPin: WallPin): Promise<WallPin> {
		this.cache.deleteTripPages(tripId)
		const apiWallPin = WallPinApiMapper.toWallPin(wallPin)

		const response: ApiWallPin | undefined = await this.post(
			`${this.resource}/${tripId}/wall_pins/`,
			apiWallPin
		)

		const result = WallPinEntityMapper.toWallPin(response!)
		this.cache.set(result!)
		return result!
	}

	static async updateWallPinData(
		tripId: string,
		wallPinId: string,
		wallPinData: Record<string, unknown>
	): Promise<WallPin> {
		this.cache.deleteTripPages(tripId)
		const response: ApiWallPin | undefined = await this.patch(
			`${this.resource}/${tripId}/wall_pins/${wallPinId}/`,
			{ data: wallPinData }
		)
		const result = WallPinEntityMapper.toWallPin(response!)
		this.cache.set(result!)
		return result!
	}

	static async deleteWallPin(tripId: string, wallPinId: string): Promise<void> {
		this.cache.delete(wallPinId)
		this.cache.deleteTripPages(tripId)
		return await this.delete(`${this.resource}/${tripId}/wall_pins/${wallPinId}/`)
	}

	static clearWallPins(tripId: string): void {
		this.cache.deleteTripPages(tripId)
	}

	static clearCaches(): void {
		this.cache.clear()
	}
}
